import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Plus, Database, ChevronRight, ChevronLeft, ArrowUpRight, Mail, Loader2, Users, Check, Unlink } from 'lucide-react';
import { colors } from '../../../../styles/tokens';
import { CampaignData } from '../types';
import { apiClient } from '../../../../lib/api';
import { useToast } from '../../../../hooks/useToast';

interface Dataset {
  id: string;
  name: string;
  lead_count?: number;
  status?: string;
  table_type?: string;
}

interface DatasetLead {
  id: string;
  name: string;
  email?: string;
  data: Record<string, any>;
}

interface DatasetColumn {
  key: string;
  name: string;
  column_type?: string;
}

interface AddRecipientsModalProps {
  isOpen: boolean;
  campaign: CampaignData;
  onClose: () => void;
  onRecipientsAdded?: () => void;
}

export const AddRecipientsModal: React.FC<AddRecipientsModalProps> = ({
  isOpen,
  campaign,
  onClose,
  onRecipientsAdded,
}) => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [availableDatasets, setAvailableDatasets] = useState<Dataset[]>([]);
  const [isLoadingDatasets, setIsLoadingDatasets] = useState(false);
  const [enrollingDatasetId, setEnrollingDatasetId] = useState<string | null>(null);
  const [showDatasetPicker, setShowDatasetPicker] = useState(false);

  // "All or part" selection state
  const [selectedDataset, setSelectedDataset] = useState<Dataset | null>(null);
  const [enrollMode, setEnrollMode] = useState<'all' | 'partial' | null>(null);

  // Partial enrollment: lead picker
  const [datasetLeads, setDatasetLeads] = useState<DatasetLead[]>([]);
  const [isLoadingLeads, setIsLoadingLeads] = useState(false);
  const [selectedLeadIds, setSelectedLeadIds] = useState<Set<string>>(new Set());

  // Email column selection
  const [datasetColumns, setDatasetColumns] = useState<DatasetColumn[]>([]);
  const [selectedEmailColumn, setSelectedEmailColumn] = useState<string>('');
  const [isLoadingColumns, setIsLoadingColumns] = useState(false);

  // Disconnect dataset
  const [disconnectingDatasetId, setDisconnectingDatasetId] = useState<string | null>(null);
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && showDatasetPicker) {
      loadAvailableDatasets();
    }
  }, [isOpen, showDatasetPicker]);

  // Reset all transient state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedDataset(null);
      setEnrollMode(null);
      setDatasetLeads([]);
      setSelectedLeadIds(new Set());
      setDatasetColumns([]);
      setSelectedEmailColumn('');
      setShowDisconnectConfirm(null);
      setDisconnectingDatasetId(null);
    }
  }, [isOpen]);

  const loadAvailableDatasets = async () => {
    setIsLoadingDatasets(true);
    try {
      const response = await apiClient.getLeadTablesPaginated(50, 0);
      // Show all completed datasets (connected ones will be grayed out)
      const available = (response.tables || []).filter(
        (t: Dataset) => t.status === 'completed'
      );
      setAvailableDatasets(available);
    } catch (error) {
      console.error('Failed to load datasets:', error);
      showToast('Failed to load datasets', 'error');
    } finally {
      setIsLoadingDatasets(false);
    }
  };

  // Check if a dataset is already connected
  const isDatasetConnected = (datasetId: string) => {
    return campaign.datasets.some(d => d.id === datasetId);
  };

  const loadDatasetColumns = useCallback(async (datasetId: string) => {
    setIsLoadingColumns(true);
    try {
      const result = await apiClient.getTableColumns(datasetId);
      const columns: DatasetColumn[] = (result.columns || []).map((col: any) => ({
        key: col.key || col.id,
        name: col.name,
        column_type: col.column_type,
      }));
      setDatasetColumns(columns);

      // Auto-select first email column if available
      const emailCol = columns.find(col =>
        col.column_type?.toLowerCase() === 'email' ||
        col.key?.toLowerCase().includes('email') ||
        col.name?.toLowerCase().includes('email')
      );
      setSelectedEmailColumn(emailCol?.key || '');
    } catch (error) {
      console.error('Failed to load dataset columns:', error);
      setDatasetColumns([]);
    } finally {
      setIsLoadingColumns(false);
    }
  }, []);

  const handleDatasetSelected = (dataset: Dataset) => {
    setSelectedDataset(dataset);
    setEnrollMode(null);
    setDatasetColumns([]);
    setSelectedEmailColumn('');
    // Load columns when dataset is selected
    loadDatasetColumns(dataset.id);
  };

  const handleBackToList = () => {
    setSelectedDataset(null);
    setEnrollMode(null);
    setDatasetLeads([]);
    setSelectedLeadIds(new Set());
    setDatasetColumns([]);
    setSelectedEmailColumn('');
  };

  const loadDatasetLeads = useCallback(async (datasetId: string) => {
    setIsLoadingLeads(true);
    try {
      const result = await apiClient.getTableLeads(datasetId);
      const leads: DatasetLead[] = (result.leads || []).map((lead: any) => ({
        id: lead.id,
        name: lead.data?.Name || lead.data?.name || lead.name || 'Unknown',
        email: lead.data?.Email || lead.data?.email || lead.data?.['Work Email'] || '',
        data: lead.data || {},
      }));
      setDatasetLeads(leads);
      setSelectedLeadIds(new Set());
    } catch (error) {
      console.error('Failed to load dataset leads:', error);
      showToast('Failed to load leads', 'error');
      setDatasetLeads([]);
    } finally {
      setIsLoadingLeads(false);
    }
  }, [showToast]);

  const handleToggleLeadSelection = useCallback((leadId: string) => {
    setSelectedLeadIds(prev => {
      const next = new Set(prev);
      if (next.has(leadId)) {
        next.delete(leadId);
      } else {
        next.add(leadId);
      }
      return next;
    });
  }, []);

  const handleToggleSelectAllLeads = useCallback(() => {
    if (selectedLeadIds.size === datasetLeads.length) {
      setSelectedLeadIds(new Set());
    } else {
      setSelectedLeadIds(new Set(datasetLeads.map(l => l.id)));
    }
  }, [selectedLeadIds.size, datasetLeads]);

  const handleDisconnectDataset = async (datasetId: string, removeRecipients: boolean = false) => {
    if (!campaign.id) return;
    setDisconnectingDatasetId(datasetId);
    try {
      await apiClient.removeDatasetFromCampaign(campaign.id, datasetId, removeRecipients);
      showToast(removeRecipients ? 'Dataset disconnected and recipients removed' : 'Dataset disconnected', 'success');
      onRecipientsAdded?.(); // Refresh campaign data
      setShowDisconnectConfirm(null);
      // Reload available datasets since the disconnected one is now available again
      if (showDatasetPicker) {
        loadAvailableDatasets();
      }
    } catch (error) {
      console.error('Failed to disconnect dataset:', error);
      showToast('Failed to disconnect dataset', 'error');
    } finally {
      setDisconnectingDatasetId(null);
    }
  };

  const handleEnroll = async () => {
    if (!campaign.id || !selectedDataset) return;

    if (enrollMode === 'partial' && selectedLeadIds.size === 0) return;

    setEnrollingDatasetId(selectedDataset.id);
    try {
      // First add the dataset to the campaign
      await apiClient.addDatasetToCampaign(campaign.id, selectedDataset.id);

      // Prepare email column option (only pass if user selected one)
      const emailColumnOption = selectedEmailColumn || undefined;

      if (enrollMode === 'partial') {
        // Enroll specific selected leads
        const result = await apiClient.enrollRecipients(
          campaign.id,
          Array.from(selectedLeadIds),
          { emailColumn: emailColumnOption }
        );
        showToast(`Enrolled ${result.enrolled_count} recipients`, 'success');
      } else {
        // Enroll all leads from the dataset
        const result = await apiClient.enrollFromDataset(
          campaign.id,
          selectedDataset.id,
          { emailColumn: emailColumnOption }
        );
        showToast(`Enrolled ${result.enrolled_count} recipients`, 'success');
      }

      onRecipientsAdded?.();
      setShowDatasetPicker(false);
      setSelectedDataset(null);
      onClose();
    } catch (error) {
      console.error('Failed to enroll from dataset:', error);
      showToast('Failed to add recipients from dataset', 'error');
    } finally {
      setEnrollingDatasetId(null);
    }
  };

  if (!isOpen) return null;

  const totalLeads = selectedDataset?.lead_count || 0;
  // Filter columns to show likely email columns first, then all others
  const emailColumns = datasetColumns.filter(col =>
    col.column_type?.toLowerCase() === 'email' ||
    col.key?.toLowerCase().includes('email') ||
    col.name?.toLowerCase().includes('email')
  );
  const otherColumns = datasetColumns.filter(col => !emailColumns.includes(col));
  const columnsForDropdown = [...emailColumns, ...otherColumns];

  const canEnroll =
    (enrollMode === 'all' || (enrollMode === 'partial' && selectedLeadIds.size > 0)) &&
    selectedEmailColumn; // Require email column selection

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div
        className="relative bg-white rounded-t-xl sm:rounded-2xl shadow-2xl w-full sm:max-w-md overflow-hidden max-h-[85vh]"
        style={{ animation: 'scaleIn 200ms cubic-bezier(0.16, 1, 0.3, 1)' }}
      >
        <div className="px-4 sm:px-6 py-4 sm:py-5" style={{ borderBottom: `1px solid ${colors.neutral[100]}` }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {selectedDataset ? (
                <button
                  onClick={handleBackToList}
                  className="w-9 sm:w-10 h-9 sm:h-10 rounded-xl flex items-center justify-center hover:bg-neutral-100 transition-colors"
                  style={{ border: `1px solid ${colors.neutral[200]}` }}
                >
                  <ChevronLeft className="w-4 sm:w-5 h-4 sm:h-5" style={{ color: colors.neutral[600] }} />
                </button>
              ) : (
                <div
                  className="w-9 sm:w-10 h-9 sm:h-10 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: colors.neutral[900] }}
                >
                  <Plus className="w-4 sm:w-5 h-4 sm:h-5 text-white" />
                </div>
              )}
              <div>
                <h3 className="text-[15px] sm:text-[16px] font-semibold" style={{ color: colors.neutral[900] }}>
                  {selectedDataset ? selectedDataset.name : 'Add Recipients'}
                </h3>
                <p className="text-[11px] sm:text-[12px]" style={{ color: colors.neutral[500] }}>
                  {selectedDataset ? `${totalLeads} leads available` : 'Expand your campaign reach'}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg sm:hidden hover:bg-neutral-100"
              style={{ color: colors.neutral[400] }}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {selectedDataset ? (
          /* Dataset enrollment options */
          <div className="p-4 sm:p-5 space-y-3">
            <p className="text-[10px] font-semibold uppercase tracking-wide mb-2 px-1" style={{ color: colors.neutral[400] }}>
              How many leads to add?
            </p>

            {/* Add all */}
            <button
              onClick={() => setEnrollMode('all')}
              className="w-full flex items-center gap-3 p-3 rounded-xl text-left transition-all group"
              style={{
                border: `1px solid ${enrollMode === 'all' ? colors.primary[600] : colors.neutral[200]}`,
                backgroundColor: enrollMode === 'all' ? colors.primary[50] : 'transparent',
              }}
            >
              <div
                className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{
                  backgroundColor: enrollMode === 'all' ? colors.primary[100] : colors.neutral[100],
                }}
              >
                <Users className="w-4 h-4" style={{ color: enrollMode === 'all' ? colors.primary[600] : colors.neutral[500] }} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[13px] font-medium" style={{ color: colors.neutral[900] }}>
                  Add all leads
                </p>
                <p className="text-[11px]" style={{ color: colors.neutral[500] }}>
                  Enroll all {totalLeads} leads from this dataset
                </p>
              </div>
              <div
                className="w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0"
                style={{
                  borderColor: enrollMode === 'all' ? colors.primary[600] : colors.neutral[300],
                }}
              >
                {enrollMode === 'all' && (
                  <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: colors.primary[600] }} />
                )}
              </div>
            </button>

            {/* Add partial */}
            <button
              onClick={() => {
                setEnrollMode('partial');
                if (selectedDataset && datasetLeads.length === 0) {
                  loadDatasetLeads(selectedDataset.id);
                }
              }}
              className="w-full flex items-center gap-3 p-3 rounded-xl text-left transition-all group"
              style={{
                border: `1px solid ${enrollMode === 'partial' ? colors.primary[600] : colors.neutral[200]}`,
                backgroundColor: enrollMode === 'partial' ? colors.primary[50] : 'transparent',
              }}
            >
              <div
                className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{
                  backgroundColor: enrollMode === 'partial' ? colors.primary[100] : colors.neutral[100],
                }}
              >
                <Database className="w-4 h-4" style={{ color: enrollMode === 'partial' ? colors.primary[600] : colors.neutral[500] }} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[13px] font-medium" style={{ color: colors.neutral[900] }}>
                  Select leads
                </p>
                <p className="text-[11px]" style={{ color: colors.neutral[500] }}>
                  Choose specific leads to enroll
                </p>
              </div>
              <div
                className="w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0"
                style={{
                  borderColor: enrollMode === 'partial' ? colors.primary[600] : colors.neutral[300],
                }}
              >
                {enrollMode === 'partial' && (
                  <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: colors.primary[600] }} />
                )}
              </div>
            </button>

            {/* Email column selection - shown when enroll mode is selected */}
            {enrollMode && (
              <div className="mt-3 p-3 rounded-xl" style={{ backgroundColor: colors.neutral[50], border: `1px solid ${colors.neutral[200]}` }}>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-[11px] font-medium" style={{ color: colors.neutral[600] }}>
                    Email column
                  </label>
                  {isLoadingColumns && (
                    <Loader2 className="w-3 h-3 animate-spin" style={{ color: colors.neutral[400] }} />
                  )}
                </div>
                <select
                  value={selectedEmailColumn}
                  onChange={(e) => setSelectedEmailColumn(e.target.value)}
                  disabled={isLoadingColumns || columnsForDropdown.length === 0}
                  className="w-full px-3 py-2 text-[12px] rounded-lg border focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-colors"
                  style={{
                    borderColor: colors.neutral[200],
                    backgroundColor: colors.white,
                    color: colors.neutral[900],
                  }}
                >
                  <option value="">Select email column...</option>
                  {emailColumns.length > 0 && (
                    <optgroup label="Email columns">
                      {emailColumns.map((col) => (
                        <option key={col.key} value={col.key}>
                          {col.name}
                        </option>
                      ))}
                    </optgroup>
                  )}
                  {otherColumns.length > 0 && (
                    <optgroup label="Other columns">
                      {otherColumns.map((col) => (
                        <option key={col.key} value={col.key}>
                          {col.name}
                        </option>
                      ))}
                    </optgroup>
                  )}
                </select>
                <p className="mt-1.5 text-[10px]" style={{ color: colors.neutral[500] }}>
                  Select the column containing recipient email addresses
                </p>
              </div>
            )}

            {enrollMode === 'partial' && (
              <div className="mt-1 rounded-xl overflow-hidden" style={{ border: `1px solid ${colors.neutral[200]}` }}>
                {isLoadingLeads ? (
                  <div className="flex items-center justify-center py-6">
                    <Loader2 className="w-5 h-5 animate-spin" style={{ color: colors.neutral[400] }} />
                  </div>
                ) : datasetLeads.length === 0 ? (
                  <p className="text-[12px] py-4 text-center" style={{ color: colors.neutral[500] }}>
                    No leads found in this dataset
                  </p>
                ) : (
                  <>
                    <div
                      className="flex items-center justify-between px-3 py-2"
                      style={{ backgroundColor: colors.neutral[50], borderBottom: `1px solid ${colors.neutral[200]}` }}
                    >
                      <button
                        onClick={handleToggleSelectAllLeads}
                        className="text-[11px] font-medium hover:underline"
                        style={{ color: colors.primary[600] }}
                      >
                        {selectedLeadIds.size === datasetLeads.length ? 'Deselect all' : 'Select all'}
                      </button>
                      <span className="text-[11px]" style={{ color: colors.neutral[500] }}>
                        {selectedLeadIds.size} of {datasetLeads.length} selected
                      </span>
                    </div>
                    <div className="max-h-48 overflow-y-auto">
                      {datasetLeads.map((lead) => {
                        const isSelected = selectedLeadIds.has(lead.id);
                        return (
                          <button
                            key={lead.id}
                            onClick={() => handleToggleLeadSelection(lead.id)}
                            className="w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors hover:bg-neutral-50"
                            style={{ borderBottom: `1px solid ${colors.neutral[100]}` }}
                          >
                            <div
                              className="w-4 h-4 rounded flex items-center justify-center flex-shrink-0 transition-colors"
                              style={{
                                backgroundColor: isSelected ? colors.primary[600] : 'transparent',
                                border: isSelected ? 'none' : `1.5px solid ${colors.neutral[300]}`,
                              }}
                            >
                              {isSelected && <Check className="w-3 h-3 text-white" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-[12px] font-medium truncate" style={{ color: colors.neutral[900] }}>
                                {lead.name}
                              </p>
                              {lead.email && (
                                <p className="text-[10px] truncate" style={{ color: colors.neutral[500] }}>
                                  {lead.email}
                                </p>
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            )}

            <div className="pt-2">
              <button
                onClick={handleEnroll}
                disabled={!canEnroll || enrollingDatasetId === selectedDataset.id}
                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-[13px] font-medium text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: colors.primary[600] }}
              >
                {enrollingDatasetId === selectedDataset.id ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Enrolling...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4" />
                    {enrollMode === 'all'
                      ? `Add ${totalLeads} recipients`
                      : enrollMode === 'partial' && selectedLeadIds.size > 0
                        ? `Add ${selectedLeadIds.size} recipients`
                        : 'Add recipients'}
                  </>
                )}
              </button>
            </div>
          </div>
        ) : (
          /* Main modal content - dataset list */
          <div className="p-4 sm:p-5 space-y-3 overflow-y-auto">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wide mb-2 px-1" style={{ color: colors.neutral[400] }}>Sources</p>
              <div className="space-y-2">
                <button
                  onClick={() => setShowDatasetPicker(!showDatasetPicker)}
                  className="w-full flex items-center gap-3 p-3 rounded-xl text-left transition-all group hover:bg-neutral-50"
                  style={{ border: `1px solid ${colors.neutral[200]}` }}
                >
                  <div
                    className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors group-hover:bg-primary-50"
                    style={{ backgroundColor: colors.neutral[100] }}
                  >
                    <Plus className="w-4 h-4 group-hover:text-primary-600" style={{ color: colors.neutral[500] }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-medium" style={{ color: colors.neutral[900] }}>Connect dataset</p>
                    <p className="text-[11px]" style={{ color: colors.neutral[500] }}>Add recipients from another dataset</p>
                  </div>
                  <ChevronRight className={`w-4 h-4 transition-transform ${showDatasetPicker ? 'rotate-90' : ''}`} style={{ color: colors.neutral[400] }} />
                </button>

                {showDatasetPicker && (
                  <div className="ml-4 space-y-2 pb-2 max-h-48 overflow-y-auto">
                    {isLoadingDatasets ? (
                      <div className="flex items-center justify-center py-4">
                        <Loader2 className="w-5 h-5 animate-spin" style={{ color: colors.neutral[400] }} />
                      </div>
                    ) : availableDatasets.length === 0 ? (
                      <p className="text-[12px] py-2 px-2" style={{ color: colors.neutral[500] }}>
                        No datasets available
                      </p>
                    ) : (
                      availableDatasets.map((dataset) => {
                        const isConnected = isDatasetConnected(dataset.id);
                        return (
                          <button
                            key={dataset.id}
                            onClick={() => !isConnected && handleDatasetSelected(dataset)}
                            disabled={isConnected}
                            className={`w-full flex items-center gap-3 p-2.5 rounded-lg text-left transition-all ${
                              isConnected ? 'cursor-not-allowed opacity-50' : 'hover:bg-neutral-50'
                            }`}
                            style={{ border: `1px solid ${colors.neutral[200]}` }}
                          >
                            <Database className="w-4 h-4 flex-shrink-0" style={{ color: colors.neutral[500] }} />
                            <div className="flex-1 min-w-0">
                              <p className="text-[12px] font-medium truncate" style={{ color: colors.neutral[900] }}>
                                {dataset.name}
                              </p>
                              <p className="text-[10px]" style={{ color: colors.neutral[500] }}>
                                {isConnected ? 'Already connected' : `${dataset.lead_count || 0} leads`}
                              </p>
                            </div>
                            {isConnected ? (
                              <Check className="w-4 h-4" style={{ color: colors.neutral[400] }} />
                            ) : (
                              <ChevronRight className="w-4 h-4" style={{ color: colors.neutral[400] }} />
                            )}
                          </button>
                        );
                      })
                    )}
                  </div>
                )}

                <button
                  disabled
                  title="Coming soon"
                  className="w-full flex items-center gap-3 p-3 rounded-xl text-left transition-all group opacity-50 cursor-not-allowed"
                  style={{ border: `1px solid ${colors.neutral[200]}` }}
                >
                  <div
                    className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors"
                    style={{ backgroundColor: colors.neutral[100] }}
                  >
                    <ArrowUpRight className="w-4 h-4" style={{ color: colors.neutral[500] }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-medium" style={{ color: colors.neutral[900] }}>Import CSV</p>
                    <p className="text-[11px]" style={{ color: colors.neutral[500] }}>Coming soon</p>
                  </div>
                </button>

                <button
                  disabled
                  title="Coming soon"
                  className="w-full flex items-center gap-3 p-3 rounded-xl text-left transition-all group opacity-50 cursor-not-allowed"
                  style={{ border: `1px solid ${colors.neutral[200]}` }}
                >
                  <div
                    className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors"
                    style={{ backgroundColor: colors.neutral[100] }}
                  >
                    <Mail className="w-4 h-4" style={{ color: colors.neutral[500] }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-medium" style={{ color: colors.neutral[900] }}>Paste emails</p>
                    <p className="text-[11px]" style={{ color: colors.neutral[500] }}>Coming soon</p>
                  </div>
                </button>
              </div>
            </div>

            {campaign.datasets.length > 0 && (
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wide mb-2 px-1" style={{ color: colors.neutral[400] }}>Connected Datasets</p>
                <div className="space-y-2">
                  {campaign.datasets.map((dataset) => (
                    <div key={dataset.id} className="relative">
                      {showDisconnectConfirm === dataset.id ? (
                        <div
                          className="p-3 rounded-xl"
                          style={{ backgroundColor: colors.neutral[50], border: `1px solid ${colors.neutral[200]}` }}
                        >
                          <p className="text-[12px] font-medium mb-2" style={{ color: colors.neutral[800] }}>
                            Disconnect "{dataset.name}"?
                          </p>
                          <p className="text-[11px] mb-3" style={{ color: colors.neutral[500] }}>
                            Recipients from this dataset will remain enrolled unless you choose to remove them.
                          </p>
                          <div className="flex flex-col gap-2">
                            <button
                              onClick={() => handleDisconnectDataset(dataset.id, false)}
                              disabled={disconnectingDatasetId === dataset.id}
                              className="w-full py-2 px-3 rounded-lg text-[12px] font-medium transition-colors hover:bg-neutral-200"
                              style={{ backgroundColor: colors.neutral[100], color: colors.neutral[700] }}
                            >
                              {disconnectingDatasetId === dataset.id ? 'Disconnecting...' : 'Disconnect only'}
                            </button>
                            <button
                              onClick={() => handleDisconnectDataset(dataset.id, true)}
                              disabled={disconnectingDatasetId === dataset.id}
                              className="w-full py-2 px-3 rounded-lg text-[12px] font-medium transition-colors hover:bg-red-600"
                              style={{ backgroundColor: colors.error?.[500] || '#EF4444', color: 'white' }}
                            >
                              Disconnect & remove recipients
                            </button>
                            <button
                              onClick={() => setShowDisconnectConfirm(null)}
                              disabled={disconnectingDatasetId === dataset.id}
                              className="w-full py-2 px-3 rounded-lg text-[12px] font-medium text-neutral-600 hover:bg-neutral-100 transition-colors"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div
                          className="flex items-center gap-3 p-3 rounded-xl"
                          style={{ backgroundColor: colors.neutral[50], border: `1px solid ${colors.neutral[200]}` }}
                        >
                          <button
                            onClick={() => { onClose(); navigate(`/datasets/${dataset.id}`); }}
                            className="flex items-center gap-3 flex-1 min-w-0 text-left"
                          >
                            <div
                              className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                              style={{ backgroundColor: colors.white, border: `1px solid ${colors.neutral[200]}` }}
                            >
                              <Database className="w-4 h-4" style={{ color: colors.neutral[600] }} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-[13px] font-medium" style={{ color: colors.neutral[900] }}>{dataset.name}</p>
                              <p className="text-[11px]" style={{ color: colors.neutral[500] }}>
                                {dataset.recipientCount ? `${dataset.recipientCount} leads` : 'View dataset'}
                              </p>
                            </div>
                          </button>
                          <button
                            onClick={() => setShowDisconnectConfirm(dataset.id)}
                            className="p-2 rounded-lg hover:bg-neutral-200 transition-colors flex-shrink-0"
                            title="Disconnect dataset"
                          >
                            <Unlink className="w-4 h-4" style={{ color: colors.neutral[400] }} />
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <button
          onClick={onClose}
          className="absolute top-5 right-5 p-1.5 text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 rounded-lg transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};
